/**
 * Математика семейств кривых: значения, характерные элементы, формулы.
 *
 * Ни одна характеристика не хранится вторым состоянием — всё считается из
 * коэффициентов. Иначе появляется способ рассинхронизировать подпись с
 * графиком, а вся затея ровно про то, что подпись и график — одно и то же.
 *
 * Вырожденные случаи (`a = 0`) не блокируются: ученик должен иметь право
 * довести коэффициент до нуля и увидеть, что получилось. Мы только называем
 * результат вслух.
 */

import {
  absExact, addExact, divExact, equalsExact, exact, formatApprox, formatExact, isNegative, isZero,
  mulExact, negExact, subExact, toNumber, type ExactNumber,
} from "./numbers";
import type {
  AbsCurve, CircleCurve, CubicCurve, CurveFamily, EllipseCurve, FunctionCurve, ParabolaCurve,
  ParameterKey, ReciprocalCurve, SqrtCurve, VertexCurve,
} from "./model";

/**
 * Координата характерной точки. `exact` отсутствует там, где число
 * иррационально — нули параболы `y = x² − 2`. Подпись тогда честно пишет «≈».
 */
export type Coordinate = { value: number; exact: ExactNumber | null };

export function exactCoordinate(value: ExactNumber): Coordinate {
  return { value: toNumber(value), exact: value };
}

export function approxCoordinate(value: number): Coordinate {
  return { value, exact: null };
}

function pretty(value: ExactNumber): string {
  return formatExact(value).replace(/^-/, "−");
}

export function coordinateText(coordinate: Coordinate): string {
  return coordinate.exact
    ? pretty(coordinate.exact)
    : `≈ ${formatApprox(coordinate.value).replace(/^-/, "−")}`;
}

export function pointText(point: { x: Coordinate; y: Coordinate }): string {
  return `(${coordinateText(point.x)}; ${coordinateText(point.y)})`;
}

export type FeatureRole =
  | "y-intercept" | "x-intercept" | "vertex" | "center" | "start" | "symmetry-center";

export type FeaturePoint = {
  roles: FeatureRole[];
  label: string;
  x: Coordinate;
  y: Coordinate;
};

export type FeatureLine =
  | { axis: "x"; at: Coordinate; role: "symmetry" | "asymptote"; label: string }
  | { axis: "y"; at: Coordinate; role: "asymptote"; label: string };

/**
 * Отрезок-помощник: радиус окружности, полуоси эллипса. Длина, которую
 * ученик ввёл числом, должна быть видна на плоскости отрезком — иначе
 * `= 9` и «радиус 3» так и остаются двумя несвязанными числами.
 */
export type FeatureSegment = {
  from: { x: number; y: number };
  to: { x: number; y: number };
  label: string;
};

export type CurveFeatures = {
  points: FeaturePoint[];
  lines: FeatureLine[];
  segments: FeatureSegment[];
  /**
   * Крайние точки замкнутой кривой — без подписей, только для кадра камеры.
   * Окружность по центру и точкам на осях в кадр целиком не попадает.
   */
  extent: { x: number; y: number }[];
  /**
   * Пояснение особого состояния или `null`, если кривая в своём семействе:
   * вырождение при нулевом коэффициенте, отрицательный радиус, эллипс,
   * ставший окружностью.
   */
  degenerate: string | null;
  /** Функция тождественно равна нулю: «пересечение с осью x» — вся ось. */
  everywhereZero: boolean;
};

const ROLE_LABELS: Record<FeatureRole, string> = {
  "y-intercept": "Пересечение с осью y",
  "x-intercept": "Пересечение с осью x",
  vertex: "Вершина",
  center: "Центр",
  start: "Начало графика",
  "symmetry-center": "Центр симметрии",
};

/** Порядок ролей в общей подписи: сначала то, что определяет кривую. */
const ROLE_ORDER: FeatureRole[] = [
  "vertex", "center", "symmetry-center", "start", "y-intercept", "x-intercept",
];

/**
 * Подпись совпавшей точки. Простое перечисление ролей верно, но в начале
 * координат даёт «Пересечение с осью y · Пересечение с осью x» — строку в две
 * трети панели. Точка на обеих осях бывает только одна, и называется она
 * короче.
 */
function combinedLabel(roles: FeatureRole[]): string {
  const sorted = [...roles].sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b));
  const bothAxes = sorted.includes("y-intercept") && sorted.includes("x-intercept");
  const parts = sorted
    .filter((role) => !(bothAxes && (role === "y-intercept" || role === "x-intercept")))
    .map((role) => ROLE_LABELS[role]);
  if (bothAxes) parts.push("Пересечение с осями");
  return parts
    .map((part, index) => (index === 0 ? part : part[0]!.toLowerCase() + part.slice(1)))
    .join(" и ");
}

/**
 * Совпавшие точки становятся одним маркером с общей подписью. Без этого
 * вершина `y = x²` и оба пересечения печатаются тремя ярлыками в одной точке.
 */
function mergePoints(raw: FeaturePoint[]): FeaturePoint[] {
  const merged: FeaturePoint[] = [];
  for (const point of raw) {
    const same = merged.find(
      (other) =>
        Math.abs(other.x.value - point.x.value) < 1e-9 &&
        Math.abs(other.y.value - point.y.value) < 1e-9,
    );
    if (same) {
      for (const role of point.roles) if (!same.roles.includes(role)) same.roles.push(role);
      same.label = combinedLabel(same.roles);
    } else {
      merged.push({ ...point, roles: [...point.roles] });
    }
  }
  return merged;
}

function feature(role: FeatureRole, x: Coordinate, y: Coordinate): FeaturePoint {
  return { roles: [role], label: ROLE_LABELS[role], x, y };
}

function perfectSquareRoot(value: number): number | null {
  if (value < 0) return null;
  const root = Math.round(Math.sqrt(value));
  return root * root === value ? root : null;
}

function perfectCubeRoot(value: number): number | null {
  const root = Math.round(Math.cbrt(value));
  return root * root * root === value ? root : null;
}

/** Точный кубический корень, если он рационален. */
export function cbrtExact(value: ExactNumber): ExactNumber | null {
  const numerator = perfectCubeRoot(value.numerator);
  const denominator = perfectCubeRoot(value.denominator);
  return numerator !== null && denominator !== null ? exact(numerator, denominator) : null;
}

/** Точный корень, если он рационален; иначе `null` — считаем приближённо. */
export function sqrtExact(value: ExactNumber): ExactNumber | null {
  if (value.numerator < 0) return null;
  const numerator = perfectSquareRoot(value.numerator);
  const denominator = perfectSquareRoot(value.denominator);
  return numerator !== null && denominator !== null ? exact(numerator, denominator) : null;
}

/** Общий вид из вида с вершиной: A = a, B = −2ah, C = ah² + k. */
export function generalForm(curve: VertexCurve): { A: ExactNumber; B: ExactNumber; C: ExactNumber } {
  const two = exact(2);
  return {
    A: curve.a,
    B: negExact(mulExact(mulExact(two, curve.a), curve.h)),
    C: addExact(mulExact(curve.a, mulExact(curve.h, curve.h)), curve.k),
  };
}

/** Раскрытие скобок: `a(x − h)² + k` → `ax² + bx + c`. Возможно всегда. */
export function toStandardForm(curve: VertexCurve): ParabolaCurve {
  const { A, B, C } = generalForm(curve);
  return { kind: "parabola", a: A, b: B, c: C };
}

/**
 * Половина коэффициента при x после деления на a: p = b/(2a). Число, вокруг
 * которого держится всё выделение полного квадрата, — и вершина, и выкладка
 * в объяснении считаются из него. `null` при a = 0: делить не на что.
 */
export function halfCoefficient(curve: ParabolaCurve): ExactNumber | null {
  return divExact(curve.b, mulExact(exact(2), curve.a));
}

/**
 * Выделение полного квадрата: `ax² + bx + c` → `a(x − h)² + k`, где
 * h = −b/(2a), k = c − b²/(4a). При a = 0 вершины нет — общий вид выродился
 * в прямую, и преобразовывать нечего.
 */
export function toVertexForm(curve: ParabolaCurve): VertexCurve | null {
  const p = halfCoefficient(curve);
  if (!p) return null;
  return {
    kind: "vertex",
    a: curve.a,
    h: negExact(p),
    k: subExact(curve.c, mulExact(curve.a, mulExact(p, p))),
  };
}

/** Рациональная форма гиперболы: y = (kx + (a − kh)) / (x − h). */
export function rationalForm(curve: ReciprocalCurve): { P: ExactNumber; Q: ExactNumber; h: ExactNumber } {
  return {
    P: curve.k,
    Q: subExact(curve.a, mulExact(curve.k, curve.h)),
    h: curve.h,
  };
}

/**
 * Решения `(t − center)² = squared`: пусто, одно или пара. Пара точная,
 * если корень рационален, иначе обе с «≈». Одно и то же уравнение даёт нули
 * параболы и пересечения окружности с осями.
 */
function rootsAround(center: ExactNumber, squared: ExactNumber): Coordinate[] {
  if (isNegative(squared)) return [];
  if (isZero(squared)) return [exactCoordinate(center)];
  const root = sqrtExact(squared);
  if (root) {
    return [exactCoordinate(subExact(center, root)), exactCoordinate(addExact(center, root))];
  }
  const value = Math.sqrt(toNumber(squared));
  return [
    approxCoordinate(toNumber(center) - value),
    approxCoordinate(toNumber(center) + value),
  ];
}

/** Признаки без помощников: у функций нет ни радиусов, ни полуосей. */
function plain(features: Omit<CurveFeatures, "segments" | "extent">): CurveFeatures {
  return { ...features, segments: [], extent: [] };
}

function lineFeatures(a: ExactNumber, b: ExactNumber): CurveFeatures {
  const points: FeaturePoint[] = [
    feature("y-intercept", exactCoordinate(exact(0)), exactCoordinate(b)),
  ];
  const root = divExact(negExact(b), a);
  if (root) points.push(feature("x-intercept", exactCoordinate(root), exactCoordinate(exact(0))));
  return plain({
    points: mergePoints(points),
    lines: [],
    degenerate: isZero(a) ? `При a = 0 это горизонтальная прямая y = ${pretty(b)}` : null,
    everywhereZero: isZero(a) && isZero(b),
  });
}

function constantFeatures(k: ExactNumber, note: string): CurveFeatures {
  return plain({
    points: mergePoints([
      feature("y-intercept", exactCoordinate(exact(0)), exactCoordinate(k)),
    ]),
    lines: [],
    degenerate: note,
    everywhereZero: isZero(k),
  });
}

function vertexFeatures(curve: VertexCurve): CurveFeatures {
  if (isZero(curve.a)) {
    return constantFeatures(
      curve.k,
      `При a = 0 это больше не парабола: получилась постоянная функция y = ${pretty(curve.k)}`,
    );
  }

  const { C } = generalForm(curve);
  const points: FeaturePoint[] = [
    feature("vertex", exactCoordinate(curve.h), exactCoordinate(curve.k)),
    feature("y-intercept", exactCoordinate(exact(0)), exactCoordinate(C)),
  ];

  // (x − h)² = −k/a. Отрицательная правая часть — действительных нулей нет.
  const squared = divExact(negExact(curve.k), curve.a);
  if (squared) {
    for (const x of rootsAround(curve.h, squared)) {
      points.push(feature("x-intercept", x, exactCoordinate(exact(0))));
    }
  }

  return plain({
    points: mergePoints(points),
    lines: [
      { axis: "x", at: exactCoordinate(curve.h), role: "symmetry", label: "Ось симметрии" },
    ],
    degenerate: null,
    everywhereZero: false,
  });
}

/**
 * Признаки общего вида — это признаки его же вида с вершиной: второго способа
 * их посчитать не существует, и заводить его значит заводить второй источник
 * правды о той же кривой.
 */
function parabolaFeatures(curve: ParabolaCurve): CurveFeatures {
  const vertex = toVertexForm(curve);
  if (!vertex) {
    return {
      ...lineFeatures(curve.b, curve.c),
      degenerate:
        `При a = 0 это больше не парабола: получилась прямая ${lineText(curve.b, curve.c)}`,
    };
  }
  return vertexFeatures(vertex);
}

function reciprocalFeatures(curve: ReciprocalCurve): CurveFeatures {
  if (isZero(curve.a)) {
    return constantFeatures(
      curve.k,
      `При a = 0 это больше не гипербола: получилась постоянная функция y = ${pretty(curve.k)}`,
    );
  }

  const points: FeaturePoint[] = [
    feature("center", exactCoordinate(curve.h), exactCoordinate(curve.k)),
  ];
  // Пересечение с осью y существует, только если сама ось не является асимптотой.
  const overH = divExact(curve.a, curve.h);
  if (overH) {
    points.push(
      feature("y-intercept", exactCoordinate(exact(0)), exactCoordinate(subExact(curve.k, overH))),
    );
  }
  // Пересечение с осью x существует, только если ось не является асимптотой.
  const overK = divExact(curve.a, curve.k);
  if (overK) {
    points.push(
      feature("x-intercept", exactCoordinate(subExact(curve.h, overK)), exactCoordinate(exact(0))),
    );
  }

  return plain({
    points: mergePoints(points),
    lines: [
      { axis: "x", at: exactCoordinate(curve.h), role: "asymptote", label: "Вертикальная асимптота" },
      { axis: "y", at: exactCoordinate(curve.k), role: "asymptote", label: "Горизонтальная асимптота" },
    ],
    degenerate: null,
    everywhereZero: false,
  });
}

/**
 * `y = a|x − h| + k`: вершина — там, где модуль обнуляется, и нули всегда
 * рациональны: |x − h| = −k/a решается без корней.
 */
function absFeatures(curve: AbsCurve): CurveFeatures {
  if (isZero(curve.a)) {
    return constantFeatures(
      curve.k,
      `При a = 0 модуль из формулы пропал: получилась постоянная функция y = ${pretty(curve.k)}`,
    );
  }
  const zero = exactCoordinate(exact(0));
  const points: FeaturePoint[] = [
    feature("vertex", exactCoordinate(curve.h), exactCoordinate(curve.k)),
    feature(
      "y-intercept", zero,
      exactCoordinate(addExact(mulExact(curve.a, absExact(curve.h)), curve.k)),
    ),
  ];
  const distance = divExact(negExact(curve.k), curve.a);
  if (distance && !isNegative(distance)) {
    points.push(feature("x-intercept", exactCoordinate(subExact(curve.h, distance)), zero));
    points.push(feature("x-intercept", exactCoordinate(addExact(curve.h, distance)), zero));
  }
  return plain({
    points: mergePoints(points),
    lines: [
      { axis: "x", at: exactCoordinate(curve.h), role: "symmetry", label: "Ось симметрии" },
    ],
    degenerate: null,
    everywhereZero: false,
  });
}

/**
 * `y = a√(x − h) + k` определена только при x ≥ h, и начало графика —
 * главная точка кривой: сдвиг по x двигает именно его, а не «весь график
 * куда-то вбок».
 */
function sqrtFeatures(curve: SqrtCurve): CurveFeatures {
  const zero = exactCoordinate(exact(0));
  const points: FeaturePoint[] = [
    feature("start", exactCoordinate(curve.h), exactCoordinate(curve.k)),
  ];
  // Ось y попадает в область определения, только если 0 ≥ h.
  if (!isNegative(negExact(curve.h))) {
    const root = sqrtExact(negExact(curve.h));
    points.push(
      feature(
        "y-intercept", zero,
        root
          ? exactCoordinate(addExact(mulExact(curve.a, root), curve.k))
          : approxCoordinate(toNumber(curve.a) * Math.sqrt(-toNumber(curve.h)) + toNumber(curve.k)),
      ),
    );
  }
  if (isZero(curve.a)) {
    return plain({
      points: mergePoints(points),
      lines: [],
      degenerate:
        `При a = 0 корень из формулы пропал, а ограничение осталось: ` +
        `это луч y = ${pretty(curve.k)} от x = ${pretty(curve.h)} вправо`,
      everywhereZero: false,
    });
  }
  // √(x − h) = −k/a: корень не бывает отрицательным, поэтому нуль один или нуля нет.
  const root = divExact(negExact(curve.k), curve.a);
  if (root && !isNegative(root)) {
    points.push(
      feature("x-intercept", exactCoordinate(addExact(curve.h, mulExact(root, root))), zero),
    );
  }
  return plain({ points: mergePoints(points), lines: [], degenerate: null, everywhereZero: false });
}

/** `y = a(x − h)³ + k`: центр симметрии в (h; k), нуль ровно один. */
function cubicFeatures(curve: CubicCurve): CurveFeatures {
  if (isZero(curve.a)) {
    return constantFeatures(
      curve.k,
      `При a = 0 это больше не кубическая парабола: получилась постоянная функция y = ${pretty(curve.k)}`,
    );
  }
  const zero = exactCoordinate(exact(0));
  const minusH = negExact(curve.h);
  const points: FeaturePoint[] = [
    feature("symmetry-center", exactCoordinate(curve.h), exactCoordinate(curve.k)),
    feature(
      "y-intercept", zero,
      exactCoordinate(addExact(mulExact(curve.a, mulExact(minusH, mulExact(minusH, minusH))), curve.k)),
    ),
  ];
  const cubed = divExact(negExact(curve.k), curve.a);
  if (cubed) {
    const root = cbrtExact(cubed);
    points.push(
      feature(
        "x-intercept",
        root
          ? exactCoordinate(addExact(curve.h, root))
          : approxCoordinate(toNumber(curve.h) + Math.cbrt(toNumber(cubed))),
        zero,
      ),
    );
  }
  return plain({ points: mergePoints(points), lines: [], degenerate: null, everywhereZero: false });
}

/**
 * Пересечения замкнутой кривой с осями — из того же уравнения, что и сама
 * кривая: подставляем y = 0 и x = 0.
 */
function axisCrossings(
  center: { h: ExactNumber; k: ExactNumber },
  onXAxis: ExactNumber | null,
  onYAxis: ExactNumber | null,
): FeaturePoint[] {
  const zero = exactCoordinate(exact(0));
  const points: FeaturePoint[] = [];
  if (onXAxis) for (const x of rootsAround(center.h, onXAxis)) points.push(feature("x-intercept", x, zero));
  if (onYAxis) for (const y of rootsAround(center.k, onYAxis)) points.push(feature("y-intercept", zero, y));
  return points;
}

function oval(h: number, k: number, rx: number, ry: number): { x: number; y: number }[] {
  return [
    { x: h - rx, y: k }, { x: h + rx, y: k }, { x: h, y: k - ry }, { x: h, y: k + ry },
  ];
}

function circleFeatures(curve: CircleCurve): CurveFeatures {
  const center = feature("center", exactCoordinate(curve.h), exactCoordinate(curve.k));
  if (isZero(curve.r)) {
    return {
      points: [center], lines: [], segments: [],
      extent: [{ x: toNumber(curve.h), y: toNumber(curve.k) }],
      degenerate: "При r = 0 окружность стягивается в одну точку — свой центр",
      everywhereZero: false,
    };
  }
  const r = absExact(curve.r);
  const r2 = mulExact(r, r);
  // (x − h)² = r² − k² на оси x и (y − k)² = r² − h² на оси y.
  const points = [
    center,
    ...axisCrossings(
      curve,
      subExact(r2, mulExact(curve.k, curve.k)),
      subExact(r2, mulExact(curve.h, curve.h)),
    ),
  ];
  const h = toNumber(curve.h);
  const k = toNumber(curve.k);
  const radius = toNumber(r);
  return {
    points: mergePoints(points),
    lines: [],
    segments: [{ from: { x: h, y: k }, to: { x: h + radius, y: k }, label: `r = ${pretty(r)}` }],
    extent: oval(h, k, radius, radius),
    degenerate: isNegative(curve.r)
      ? `Радиус — длина, отрицательным он не бывает. В уравнение входит r², ` +
        `поэтому это та же окружность, что при r = ${pretty(r)}`
      : null,
    everywhereZero: false,
  };
}

function ellipseFeatures(curve: EllipseCurve): CurveFeatures {
  if (isZero(curve.a) || isZero(curve.b)) {
    const letter = isZero(curve.a) ? "a" : "b";
    return {
      points: [], lines: [], segments: [], extent: [],
      degenerate: `При ${letter} = 0 уравнение теряет смысл: ${letter}² стоит в знаменателе`,
      everywhereZero: false,
    };
  }
  const a = absExact(curve.a);
  const b = absExact(curve.b);
  const a2 = mulExact(a, a);
  const b2 = mulExact(b, b);
  const one = exact(1);
  // y = 0: (x − h)² = a²·(1 − k²/b²); x = 0: (y − k)² = b²·(1 − h²/a²).
  const onXAxis = mulExact(a2, subExact(one, divExact(mulExact(curve.k, curve.k), b2)!));
  const onYAxis = mulExact(b2, subExact(one, divExact(mulExact(curve.h, curve.h), a2)!));
  const points = [
    feature("center", exactCoordinate(curve.h), exactCoordinate(curve.k)),
    ...axisCrossings(curve, onXAxis, onYAxis),
  ];

  const notes: string[] = [];
  const negative = [isNegative(curve.a) ? "a" : null, isNegative(curve.b) ? "b" : null]
    .filter((letter): letter is string => letter !== null);
  if (negative.length > 0) {
    notes.push(
      `Полуось — длина, отрицательной она не бывает. В уравнение входит ` +
      `${negative.map((letter) => `${letter}²`).join(" и ")}, поэтому график тот же, что при ` +
      `${negative.map((letter) => `${letter} = ${pretty(letter === "a" ? a : b)}`).join(", ")}`,
    );
  }
  if (equalsExact(a, b)) {
    notes.push(`При равных полуосях эллипс — это окружность радиуса ${pretty(a)}`);
  }

  const h = toNumber(curve.h);
  const k = toNumber(curve.k);
  const rx = toNumber(a);
  const ry = toNumber(b);
  return {
    points: mergePoints(points),
    lines: [],
    segments: [
      { from: { x: h, y: k }, to: { x: h + rx, y: k }, label: `a = ${pretty(a)}` },
      { from: { x: h, y: k }, to: { x: h, y: k + ry }, label: `b = ${pretty(b)}` },
    ],
    extent: oval(h, k, rx, ry),
    degenerate: notes.length > 0 ? notes.join(". ") : null,
    everywhereZero: false,
  };
}

export function curveFeatures(curve: FunctionCurve): CurveFeatures {
  switch (curve.kind) {
    case "line":
      return lineFeatures(curve.a, curve.b);
    case "parabola":
      return parabolaFeatures(curve);
    case "vertex":
      return vertexFeatures(curve);
    case "reciprocal":
      return reciprocalFeatures(curve);
    case "abs":
      return absFeatures(curve);
    case "sqrt":
      return sqrtFeatures(curve);
    case "cubic":
      return cubicFeatures(curve);
    case "circle":
      return circleFeatures(curve);
    case "ellipse":
      return ellipseFeatures(curve);
  }
}

/**
 * Значение функции в точке. `null` — значение не определено: гипербола на
 * своей асимптоте, корень левее начала графика. У окружности и эллипса
 * единственного значения нет, для них всегда `null` — спрашивать `valuesAt`.
 */
export function valueAt(curve: FunctionCurve, x: number): number | null {
  switch (curve.kind) {
    case "line":
      return toNumber(curve.a) * x + toNumber(curve.b);
    case "parabola":
      return toNumber(curve.a) * x * x + toNumber(curve.b) * x + toNumber(curve.c);
    case "vertex": {
      const shift = x - toNumber(curve.h);
      return toNumber(curve.a) * shift * shift + toNumber(curve.k);
    }
    case "reciprocal": {
      const shift = x - toNumber(curve.h);
      if (shift === 0) return null;
      const value = toNumber(curve.a) / shift + toNumber(curve.k);
      return Number.isFinite(value) ? value : null;
    }
    case "abs":
      return toNumber(curve.a) * Math.abs(x - toNumber(curve.h)) + toNumber(curve.k);
    case "sqrt": {
      const shift = x - toNumber(curve.h);
      if (shift < 0) return null;
      return toNumber(curve.a) * Math.sqrt(shift) + toNumber(curve.k);
    }
    case "cubic": {
      const shift = x - toNumber(curve.h);
      return toNumber(curve.a) * shift * shift * shift + toNumber(curve.k);
    }
    case "circle":
    case "ellipse":
      return null;
  }
}

/**
 * Все `y` кривой при данном `x`, по возрастанию. Для функции — ноль или одно
 * значение, для окружности и эллипса — ноль, одно (касание) или два:
 * вертикальная прямая пересекает их дважды, и именно это делает их не
 * функциями.
 */
export function valuesAt(curve: FunctionCurve, x: number): number[] {
  if (curve.kind !== "circle" && curve.kind !== "ellipse") {
    const value = valueAt(curve, x);
    return value === null ? [] : [value];
  }
  const h = toNumber(curve.h);
  const k = toNumber(curve.k);
  const rx = Math.abs(toNumber(curve.kind === "circle" ? curve.r : curve.a));
  const ry = Math.abs(toNumber(curve.kind === "circle" ? curve.r : curve.b));
  if (rx === 0 || ry === 0) return [];
  const ratio = (x - h) / rx;
  const rest = 1 - ratio * ratio;
  if (rest < 0) return [];
  if (rest === 0) return [k];
  const offset = ry * Math.sqrt(rest);
  return [k - offset, k + offset];
}

/** Точка разрыва — единственный `x`, в котором функция не определена. */
export function discontinuityAt(curve: FunctionCurve): number | null {
  return curve.kind === "reciprocal" && !isZero(curve.a) ? toNumber(curve.h) : null;
}

export const MINUS = "−";

/** Свободный член в хвосте записи: `` для нуля, ` − 4`, ` + 4`. */
export function tail(value: ExactNumber): string {
  if (isZero(value)) return "";
  return isNegative(value)
    ? ` ${MINUS} ${formatExact(absExact(value))}`
    : ` + ${formatExact(value)}`;
}

function isOne(value: ExactNumber): boolean {
  return value.numerator === 1 && value.denominator === 1;
}

function isMinusOne(value: ExactNumber): boolean {
  return value.numerator === -1 && value.denominator === 1;
}

/** Коэффициент перед переменной: `1` и `−1` в записи не печатаются. */
export function withVariable(value: ExactNumber, variable: string): string {
  if (isOne(value)) return variable;
  if (isMinusOne(value)) return `${MINUS}${variable}`;
  return `${pretty(value)}${variable}`;
}

/** Слагаемое со знаком в середине записи: ` − 6x`, ` + 6x`, `` для нуля. */
export function signedTerm(value: ExactNumber, variable: string): string {
  if (isZero(value)) return "";
  return isNegative(value)
    ? ` ${MINUS} ${withVariable(absExact(value), variable)}`
    : ` + ${withVariable(value, variable)}`;
}

/** Отдельное число тем же минусом, каким его печатают формулы. */
export function numberText(value: ExactNumber): string {
  return pretty(value);
}

/** Переменная со сдвигом: `x − 3`, `y + 2`, просто `x` при нуле. */
export function shifted(value: ExactNumber, variable: string): string {
  if (isZero(value)) return variable;
  return isNegative(value)
    ? `${variable} + ${formatExact(absExact(value))}`
    : `${variable} ${MINUS} ${formatExact(value)}`;
}

export function shiftedX(h: ExactNumber): string {
  return shifted(h, "x");
}

/** Квадрат сдвинутой переменной: `x²` без сдвига, `(x − 3)²` со сдвигом. */
function squareOf(value: ExactNumber, variable: string): string {
  return isZero(value) ? `${variable}²` : `(${shifted(value, variable)})²`;
}

/** Знаменатель после «/»: дробь в скобках, иначе `x²/1/4` читается двояко. */
function overText(value: ExactNumber): string {
  const text = pretty(value);
  return text.includes("/") ? `(${text})` : text;
}

function lineText(a: ExactNumber, b: ExactNumber): string {
  if (isZero(a)) return `y = ${pretty(b)}`;
  return `y = ${withVariable(a, "x")}${tail(b)}`;
}

/** Запись общего вида числами: `y = 2x² − 12x + 13`. */
export function standardText(a: ExactNumber, b: ExactNumber, c: ExactNumber): string {
  if (isZero(a)) return lineText(b, c);
  return `y = ${withVariable(a, "x²")}${signedTerm(b, "x")}${tail(c)}`;
}

/** Редактируемая форма кривой. */
export function formulaText(curve: FunctionCurve): string {
  switch (curve.kind) {
    case "line":
      return lineText(curve.a, curve.b);
    case "parabola":
      return standardText(curve.a, curve.b, curve.c);
    case "vertex": {
      if (isZero(curve.a)) return `y = ${pretty(curve.k)}`;
      const base = isZero(curve.h) ? "x²" : `(${shiftedX(curve.h)})²`;
      return `y = ${withVariable(curve.a, base)}${tail(curve.k)}`;
    }
    case "reciprocal": {
      if (isZero(curve.a)) return `y = ${pretty(curve.k)}`;
      const denominator = isZero(curve.h) ? "x" : `(${shiftedX(curve.h)})`;
      return `y = ${pretty(curve.a)}/${denominator}${tail(curve.k)}`;
    }
    case "abs": {
      if (isZero(curve.a)) return `y = ${pretty(curve.k)}`;
      return `y = ${withVariable(curve.a, `|${shiftedX(curve.h)}|`)}${tail(curve.k)}`;
    }
    case "sqrt": {
      // Без корня в записи пропало бы и ограничение x ≥ h, а график — луч.
      if (isZero(curve.a)) return `y = ${pretty(curve.k)}, x ≥ ${pretty(curve.h)}`;
      const base = isZero(curve.h) ? "√x" : `√(${shiftedX(curve.h)})`;
      return `y = ${withVariable(curve.a, base)}${tail(curve.k)}`;
    }
    case "cubic": {
      if (isZero(curve.a)) return `y = ${pretty(curve.k)}`;
      const base = isZero(curve.h) ? "x³" : `(${shiftedX(curve.h)})³`;
      return `y = ${withVariable(curve.a, base)}${tail(curve.k)}`;
    }
    case "circle":
      return `${squareOf(curve.h, "x")} + ${squareOf(curve.k, "y")} = ` +
        pretty(mulExact(curve.r, curve.r));
    case "ellipse": {
      const part = (shift: ExactNumber, variable: string, axis: ExactNumber) => {
        const square = mulExact(axis, axis);
        const over = isOne(square) ? "" : `/${overText(square)}`;
        return `${squareOf(shift, variable)}${over}`;
      };
      return `${part(curve.h, "x", curve.a)} + ${part(curve.k, "y", curve.b)} = 1`;
    }
  }
}

/**
 * Формула семейства, разобранная на куски: текст и места коэффициентов.
 *
 * Один источник и для подписи `y = ax + b`, и для строки с полями ввода —
 * иначе шапка кривой и редактируемая строка расходятся в записи, и ученик
 * сверяет две разные формулы вместо одной.
 *
 * Знак здесь всегда «+», а не подстроенный под знак коэффициента: в поле
 * стоит само число со своим минусом, и «+ −4» честнее, чем «− 4», где минус
 * принадлежит то формуле, то значению.
 */
export type FormulaPart = { text: string } | { param: ParameterKey };

const TEMPLATES: Record<CurveFamily, FormulaPart[]> = {
  line: [{ text: "y = " }, { param: "a" }, { text: "x + " }, { param: "b" }],
  parabola: [
    { text: "y = " }, { param: "a" }, { text: "x² + " }, { param: "b" },
    { text: "x + " }, { param: "c" },
  ],
  vertex: [
    { text: "y = " }, { param: "a" }, { text: `(x ${MINUS} ` }, { param: "h" },
    { text: ")² + " }, { param: "k" },
  ],
  reciprocal: [
    { text: "y = " }, { param: "a" }, { text: `/(x ${MINUS} ` }, { param: "h" },
    { text: ") + " }, { param: "k" },
  ],
  abs: [
    { text: "y = " }, { param: "a" }, { text: `|x ${MINUS} ` }, { param: "h" },
    { text: "| + " }, { param: "k" },
  ],
  sqrt: [
    { text: "y = " }, { param: "a" }, { text: `√(x ${MINUS} ` }, { param: "h" },
    { text: ") + " }, { param: "k" },
  ],
  cubic: [
    { text: "y = " }, { param: "a" }, { text: `(x ${MINUS} ` }, { param: "h" },
    { text: ")³ + " }, { param: "k" },
  ],
  circle: [
    { text: `(x ${MINUS} ` }, { param: "h" }, { text: `)² + (y ${MINUS} ` }, { param: "k" },
    { text: ")² = " }, { param: "r" }, { text: "²" },
  ],
  ellipse: [
    { text: `(x ${MINUS} ` }, { param: "h" }, { text: ")²/" }, { param: "a" },
    { text: `² + (y ${MINUS} ` }, { param: "k" }, { text: ")²/" }, { param: "b" },
    { text: "² = 1" },
  ],
};

export function formulaTemplate(family: CurveFamily): FormulaPart[] {
  return TEMPLATES[family];
}

/** Та же формула буквами: `y = ax + b`. Подпись семейства, а не состояние. */
export function familyShape(family: CurveFamily): string {
  return TEMPLATES[family]
    .map((part) => ("text" in part ? part.text : part.param))
    .join("");
}

/**
 * Вторая, вычисленная форма — она показывается рядом и не редактируется.
 * У прямой второй формы нет.
 */
export function equivalentForm(curve: FunctionCurve): { label: string; text: string } | null {
  if (curve.kind === "vertex") {
    const { A, B, C } = generalForm(curve);
    return { label: "Общий вид", text: standardText(A, B, C) };
  }
  if (curve.kind === "parabola") {
    const vertex = toVertexForm(curve);
    return vertex ? { label: "Вид с вершиной", text: formulaText(vertex) } : null;
  }
  if (curve.kind === "reciprocal") {
    if (isZero(curve.a)) return null;
    const { P, Q, h } = rationalForm(curve);
    const numerator = isZero(P) ? pretty(Q) : `${withVariable(P, "x")}${tail(Q)}`;
    return { label: "Рациональная форма", text: `y = (${numerator})/(${shiftedX(h)})` };
  }
  if (curve.kind === "circle") {
    // x² + y² − 2hx − 2ky + (h² + k² − r²) = 0 — вид, в котором окружность
    // обычно приходит в задаче «найдите центр и радиус».
    const two = exact(2);
    const free = subExact(
      addExact(mulExact(curve.h, curve.h), mulExact(curve.k, curve.k)),
      mulExact(curve.r, curve.r),
    );
    return {
      label: "Без скобок",
      text: `x² + y²${signedTerm(negExact(mulExact(two, curve.h)), "x")}` +
        `${signedTerm(negExact(mulExact(two, curve.k)), "y")}${tail(free)} = 0`,
    };
  }
  return null;
}

/** Текстовое описание кривой для screen reader и подписи экспорта. */
export function describeCurve(curve: FunctionCurve): string {
  const features = curveFeatures(curve);
  const parts = [formulaText(curve)];
  if (features.degenerate) parts.push(features.degenerate);
  for (const point of features.points) parts.push(`${point.label} ${pointText(point)}`);
  for (const line of features.lines) {
    parts.push(`${line.label} ${line.axis} = ${coordinateText(line.at)}`);
  }
  for (const segment of features.segments) parts.push(segment.label);
  if (features.everywhereZero) parts.push("График совпадает с осью x");
  return parts.join(". ");
}
